import React, { ReactNode, useEffect, useMemo, useState } from 'react';
import { OnboardingTPV } from 'modules/status/components/Onboarding/TPV/OnboardingTPV';
import { useVerifyAchData } from 'api/agreement';
import { MyEverBrightBanner } from 'modules/status/components/Onboarding/MyEverBright/MyEverBrightBanner/MyEverBrightBanner';
import MyEverBrightModal from 'modules/status/components/Onboarding/MyEverBright/MyEverBrightModal/MyEverBrightModal';
import { LoadingSpinner } from '../../../../shared/components/LoadingSpinner';
import { OnboardingContainer } from './Onboarding.styles';
import { useUser } from '../../../../hooks/useUser';
import { OnboardingIntroduction } from '../../components/Onboarding/Introduction/OnboardingIntroduction';
import { useOnboardingStep } from '../../../../hooks/useOnboardingStep';
import { MilestoneType, OnboardingStep } from '../../../types/util';
import { OnboardingHeader } from './OnboardingHeader';
import { OnboardingMilestone } from '../../components/Onboarding/Milestone/OnboardingMilestone';
import { ReviewAgreement } from '../ReviewAgreement/ReviewAgreement';
import { ConfirmBillingAddress } from '../ConfirmBillingAddress/ConfirmBillingAddress';
import { OnboardingAutoPay } from '../../components/Onboarding/AutoPay/OnboardingAutoPay';

const getOnboardingContent = ({
  onShowBanner,
}: {
  onShowBanner: () => void;
}): Record<OnboardingStep, ReactNode> => ({
  [OnboardingStep.INTRODUCTION]: <OnboardingIntroduction onShowBanner={onShowBanner} />,
  [OnboardingStep.PRE_INSTALLATION]: <OnboardingMilestone />,
  [OnboardingStep.REVIEW_AGREEMENT]: <ReviewAgreement />,
  [OnboardingStep.TPV]: <OnboardingTPV />,
  [OnboardingStep.AUTOPAY]: <OnboardingAutoPay />,
  [OnboardingStep.CONFIRM_ADDRESS]: <ConfirmBillingAddress />,
  [OnboardingStep.SYSTEM_INSTALLATION]: <OnboardingMilestone />,
  [OnboardingStep.SYSTEM_ACTIVATION]: <OnboardingMilestone />,
});

export function Onboarding() {
  const { userResult, selectedAgreement, refetch: refetchUser } = useUser();
  const { currentStep } = useOnboardingStep();
  const [attemptedAchVerification, setAttemptedAchVerification] = useState(false);
  const [appBannerVisible, setAppBannerVisible] = useState(false);
  const [myEverBrightModalOpen, setMyEverBrightModalOpen] = useState(false);

  const isLoading = !userResult || userResult.loading;

  const { mutateAsync: verifyAchDataAsync } = useVerifyAchData({ onSuccess: refetchUser });

  // attempt to check ACH verification status if we're at M0 and were
  // not able to retrieve ACH info during import
  useEffect(() => {
    const asyncEffect = async () => {
      if (selectedAgreement && !attemptedAchVerification) {
        const {
          current_milestone: currentMilestone,
          ach_elected: achElected,
          ach_verified: achVerified,
          id: agreementId,
        } = selectedAgreement;
        if (
          currentMilestone === MilestoneType.CONTRACT_SIGNED &&
          achElected !== false &&
          achVerified === null
        ) {
          await verifyAchDataAsync({ agreement_id: agreementId, fetch_bank_details: true });
          // prevents multiple attempts if we're unable to complete the verification process
          setAttemptedAchVerification(true);
        }
      }
    };
    asyncEffect();
  }, [selectedAgreement, verifyAchDataAsync, attemptedAchVerification]);

  const onboardingContent = useMemo(
    () => getOnboardingContent({ onShowBanner: () => setAppBannerVisible(true) }),
    [setAppBannerVisible],
  );

  return (
    <OnboardingContainer>
      {isLoading ? (
        <LoadingSpinner />
      ) : (
        <>
          <MyEverBrightBanner
            isVisible={appBannerVisible}
            onClose={() => setAppBannerVisible(false)}
            onOpenModal={() => setMyEverBrightModalOpen(true)}
          />
          <MyEverBrightModal
            isOpen={myEverBrightModalOpen}
            onClose={() => {
              setAppBannerVisible(false);
              setMyEverBrightModalOpen(false);
            }}
          />
          <OnboardingHeader />
          {onboardingContent[currentStep]}
        </>
      )}
    </OnboardingContainer>
  );
}

export default Onboarding;
